import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, ChevronDown, ChevronUp } from 'lucide-react';

import Button from '../../../components/ui/Button';
import PersonCell from '../../../components/ui/PersonCell';

const Card = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  cursor: pointer;
`;

const Name = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MoveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 10px;
  background: rgba(234, 88, 12, 0.1);
  color: #EA580C;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
`;

const Department = styled.div`
  font-size: 12px;
  color: #64748B;
  margin-top: 2px;
`;

const ScoresRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 6px;
`;

const Score = styled.span`
  font-size: 11px;
  color: #004242;
  background: #F5F7FA;
  border-radius: 4px;
  padding: 2px 6px;
`;

const DetailPanel = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #E8ECF0;
  font-size: 12px;
  color: #64748B;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MoveButton = styled(Button)`
  margin-top: 8px;
  width: 100%;
  padding: 8px 12px;
  font-size: 12px;
`;

const formatScore = (v) => (v === null || v === undefined ? '-' : Number(v).toFixed(1));

// Faz B (spec 2026-08-07 §4, Task 5) — ızgara entry kartı: ad/departman/A-B
// skorları + taşıma rozeti + detay paneli (taşıma geçmişi — grid DTO'sunda
// yalnız lastMoveNote/moveCount var, EKSTRA UÇ ÇAĞRILMAZ, bkz. brief).
export default function EntryCard({ entry, canMove, onMoveClick }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <Card data-role="entry-card" data-record-id={entry.calibrationRecordId}>
      <HeaderRow data-role="entry-card-header" onClick={() => setExpanded((v) => !v)}>
        <Name>
          <PersonCell
            name={entry.employeeName}
            profilePicture={entry.employeeProfilePicture}
            size={22}
          />
        </Name>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {entry.moveCount > 0 && (
            <MoveBadge data-role="move-badge">
              <ArrowRightLeft size={10} />
              {entry.moveCount}
            </MoveBadge>
          )}
          {expanded ? <ChevronUp size={14} color="#A0A8B4" /> : <ChevronDown size={14} color="#A0A8B4" />}
        </div>
      </HeaderRow>
      {entry.departmentName && <Department>{entry.departmentName}</Department>}
      <ScoresRow>
        <Score>A: {formatScore(entry.systemAScore)}</Score>
        <Score>B: {formatScore(entry.systemBScore)}</Score>
      </ScoresRow>
      {expanded && (
        <DetailPanel data-role="entry-detail-panel">
          <span>{t('performance.calibrationSession.grid.moveCountLabel')}: {entry.moveCount}</span>
          {entry.lastMoveNote && (
            <span>{t('performance.calibrationSession.grid.lastMoveNoteLabel')}: {entry.lastMoveNote}</span>
          )}
        </DetailPanel>
      )}
      {canMove && (
        <MoveButton
          data-role="move-entry-btn"
          type="button"
          variant="outline"
          onClick={(e) => { e.stopPropagation(); onMoveClick(entry); }}
        >
          <ArrowRightLeft size={13} />
          {t('performance.calibrationSession.grid.moveButton')}
        </MoveButton>
      )}
    </Card>
  );
}
